// src/pages/admin/AuditLogDetailsDialog.jsx
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Divider,
    Box,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Stack
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ContentCopy as CopyIcon,
    Person as PersonIcon,
    Event as EventIcon,
    Assignment as AssignmentIcon,
    LocalHospital as ScanIcon,
    AttachMoney as MoneyIcon,
    Info as InfoIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

// Tab Panel Component
const TabPanel = ({ children, value, index, ...other }) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`audit-tabpanel-${index}`}
        aria-labelledby={`audit-tab-${index}`}
        {...other}
    >
        {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
);

const AuditLogDetailsDialog = ({ open, onClose, log }) => {
    const { t } = useTranslation();
    const [tabValue, setTabValue] = useState(0);
    const [changesExpanded, setChangesExpanded] = useState(true);

    if (!log) return null;

    // Helper Functions
    const getActionChip = (action) => {
        const config = {
            CREATE: { color: 'success', label: t('audit.create', 'Create') },
            UPDATE: { color: 'primary', label: t('audit.update', 'Update') },
            DELETE: { color: 'error', label: t('audit.delete', 'Delete') },
            STATUS_CHANGE: { color: 'warning', label: t('audit.statusChange', 'Status Change') }
        };
        const { color, label } = config[action] || { color: 'default', label: action };
        return <Chip label={label} color={color} size="small" />;
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        try {
            return format(new Date(timestamp), 'dd/MM/yyyy HH:mm:ss');
        } catch {
            return String(timestamp);
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            return format(new Date(date), 'dd/MM/yyyy');
        } catch {
            return String(date);
        }
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '-';
        return new Intl.NumberFormat('ar-EG', {
            style: 'currency',
            currency: 'EGP',
        }).format(amount);
    };

    const formatBoolean = (value) => {
        if (value === true) {
            return <Chip icon={<CheckIcon />} label={t('common.yes', 'Yes')} color="success" size="small" />;
        }
        if (value === false) {
            return <Chip icon={<CancelIcon />} label={t('common.no', 'No')} color="default" size="small" variant="outlined" />;
        }
        return '-';
    };

    const formatValue = (value, key = '') => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return formatBoolean(value);
        if (key.toLowerCase().includes('price') || key.toLowerCase().includes('cost') ||
            key.toLowerCase().includes('fee') || key.toLowerCase().includes('profit') ||
            key.toLowerCase().includes('amount') || key.toLowerCase().includes('discount')) {
            return formatCurrency(value);
        }
        if (key.toLowerCase().includes('date') || key.toLowerCase().includes('at')) {
            return formatTimestamp(value);
        }
        if (typeof value === 'object') {
            if (Array.isArray(value)) return `[${value.length} items]`;
            return JSON.stringify(value);
        }
        return String(value);
    };

    const handleCopyChanges = () => {
        if (log.changes) {
            navigator.clipboard.writeText(JSON.stringify(log.changes, null, 2));
            toast.success(t('common.copied', 'Copied to clipboard'));
        }
    };

    const handleCopyAll = () => {
        navigator.clipboard.writeText(JSON.stringify(log, null, 2));
        toast.success(t('common.copied', 'Copied to clipboard'));
    };

    // Get changes data (could be in log.changes or directly in log for CREATE)
    const changesData = log.changes || {};
    const appointmentData = changesData.created || changesData.after || changesData.before || changesData;

    // Field Labels Mapping
    const fieldLabels = {
        radiologistId: t('audit.fields.radiologist', 'Radiologist'),
        radiologyTechnician: t('audit.fields.technician', 'Technician'),
        branch: t('audit.fields.branch', 'Branch'),
        patientId: t('audit.fields.patient', 'Patient'),
        referredBy: t('audit.fields.referredBy', 'Referred By'),
        scheduledAt: t('audit.fields.scheduledAt', 'Scheduled At'),
        status: t('audit.fields.status', 'Status'),
        priority: t('audit.fields.priority', 'Priority'),
        notes: t('audit.fields.notes', 'Notes'),
        cost: t('audit.fields.cost', 'Cost'),
        price: t('audit.fields.price', 'Price'),
        profit: t('audit.fields.profit', 'Profit'),
        makeHugeSale: t('audit.fields.hugeSale', 'Huge Sale'),
        customPrice: t('audit.fields.customPrice', 'Custom Price'),
        discountAmount: t('audit.fields.discount', 'Discount'),
        completionFee: t('audit.fields.completionFee', 'Completion Fee'),
        salePercentage: t('audit.fields.salePercentage', 'Sale %'),
        isCompleted: t('audit.fields.isCompleted', 'Completed'),
        isPaid: t('audit.fields.isPaid', 'Paid'),
        isActive: t('audit.fields.isActive', 'Active'),
        createdBy: t('audit.fields.createdBy', 'Created By'),
        createdAt: t('audit.fields.createdAt', 'Created At'),
        updatedAt: t('audit.fields.updatedAt', 'Updated At'),
        _id: t('audit.fields.id', 'ID'),
        id: t('audit.fields.id', 'ID'),
    };

    // Group fields by category
    const groupFields = (data) => {
        if (!data || typeof data !== 'object') return {};

        const groups = {
            basic: {
                title: t('audit.groups.basicInfo', 'Basic Information'),
                icon: <InfoIcon />,
                fields: ['_id', 'id', 'status', 'priority', 'notes', 'isActive']
            },
            patient: {
                title: t('audit.groups.patientInfo', 'Patient & References'),
                icon: <PersonIcon />,
                fields: ['patientId', 'referredBy', 'radiologistId', 'radiologyTechnician', 'branch', 'createdBy']
            },
            schedule: {
                title: t('audit.groups.schedule', 'Schedule'),
                icon: <EventIcon />,
                fields: ['scheduledAt', 'createdAt', 'updatedAt']
            },
            financial: {
                title: t('audit.groups.financial', 'Financial'),
                icon: <MoneyIcon />,
                fields: ['price', 'cost', 'profit', 'discountAmount', 'completionFee', 'customPrice', 'salePercentage', 'makeHugeSale', 'isPaid', 'isCompleted']
            },
            scans: {
                title: t('audit.groups.scans', 'Scans'),
                icon: <ScanIcon />,
                fields: ['scans']
            }
        };

        const result = {};
        Object.entries(groups).forEach(([groupKey, group]) => {
            const groupData = {};
            group.fields.forEach(field => {
                if (data.hasOwnProperty(field) && data[field] !== undefined) {
                    groupData[field] = data[field];
                }
            });
            if (Object.keys(groupData).length > 0) {
                result[groupKey] = { ...group, data: groupData };
            }
        });

        return result;
    };

    // Render a simple info table
    const renderInfoTable = (data, excludeFields = []) => {
        const entries = Object.entries(data).filter(([key]) =>
            !excludeFields.includes(key) && key !== 'scans' && key !== '__v'
        );

        if (entries.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {t('common.noData', 'No data available')}
                </Typography>
            );
        }

        return (
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow >
                            <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                                {t('common.field', 'Field')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('common.value', 'Value')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.map(([key, value], index) => (
                            <TableRow
                                key={key}
                            >
                                <TableCell sx={{ fontWeight: 500 }}>
                                    {fieldLabels[key] || key}
                                </TableCell>
                                <TableCell>
                                    {formatValue(value, key)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // Render scans table
    const renderScansTable = (scans) => {
        if (!scans || !Array.isArray(scans) || scans.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {t('audit.noScans', 'No scans recorded')}
                </Typography>
            );
        }

        return (
            <Box>
                {scans.map((scan, scanIndex) => (
                    <Paper key={scan._id || scanIndex} variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                        {/* Scan Header */}
                        <Box sx={{ p: 2, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2">
                                    {t('audit.scan', 'Scan')} #{scanIndex + 1}
                                </Typography>
                                <Chip
                                    label={`${t('common.quantity', 'Qty')}: ${scan.quantity || 1}`}
                                    size="small"
                                    sx={{ backgroundColor: 'white' }}
                                />
                            </Stack>
                        </Box>

                        {/* Scan Details */}
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500, width: '30%' }}>
                                            {t('audit.fields.scanId', 'Scan ID')}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace">
                                                {scan.scan || '-'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            {t('common.quantity', 'Quantity')}
                                        </TableCell>
                                        <TableCell>{scan.quantity || 1}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Scan Items */}
                        {scan.items && scan.items.length > 0 && (
                            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ p: 1.5 }}>
                                    <Typography variant="subtitle2">
                                        {t('audit.consumables', 'Consumables')} ({scan.items.length})
                                    </Typography>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>
                                                    {t('common.item', 'Item')}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                                                    {t('common.quantity', 'Quantity')}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {scan.items.map((item, itemIndex) => (
                                                <TableRow
                                                    key={item._id || itemIndex}
                                                >
                                                    <TableCell>{itemIndex + 1}</TableCell>
                                                    <TableCell>{item.item || '-'}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={item.quantity || 0}
                                                            size="small"
                                                            color={item.quantity > 0 ? 'primary' : 'default'}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </Paper>
                ))}
            </Box>
        );
    };

    // Render changes comparison table (for UPDATE action)
    const renderChangesComparison = () => {
        if (!log.changes) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {t('audit.noChangesRecorded', 'No changes recorded')}
                </Typography>
            );
        }

        // UPDATE: before/after comparison
        if (log.changes.before && log.changes.after) {
            const allKeys = [...new Set([
                ...Object.keys(log.changes.before),
                ...Object.keys(log.changes.after)
            ])].filter(key => key !== '__v' && key !== 'updatedAt');

            const changedKeys = allKeys.filter(key => {
                const before = JSON.stringify(log.changes.before[key]);
                const after = JSON.stringify(log.changes.after[key]);
                return before !== after;
            });

            if (changedKeys.length === 0) {
                return (
                    <Typography variant="body2" color="text.secondary">
                        {t('audit.noChangesDetected', 'No changes detected')}
                    </Typography>
                );
            }

            return (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow >
                                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>
                                    {t('common.field', 'Field')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '35%', color: 'error.main' }}>
                                    {t('audit.before', 'Before')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '5%' }} align="center">
                                    →
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '35%', color: 'success.main' }}>
                                    {t('audit.after', 'After')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {changedKeys.map((key, index) => (
                                <TableRow
                                    key={key}
                                >
                                    <TableCell sx={{ fontWeight: 500 }}>
                                        {fieldLabels[key] || key}
                                    </TableCell>
                                    <TableCell sx={{ color: 'error.main', wordBreak: 'break-word' }}>
                                        {formatValue(log.changes.before[key], key)}
                                    </TableCell>
                                    <TableCell align="center">→</TableCell>
                                    <TableCell sx={{ color: 'success.main', wordBreak: 'break-word' }}>
                                        {formatValue(log.changes.after[key], key)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }

        // STATUS_CHANGE: from/to
        if (log.changes.from !== undefined && log.changes.to !== undefined) {
            return (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500, width: '30%' }}>
                                    {t('audit.statusChange', 'Status Change')}
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip
                                            label={log.changes.from}
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                        />
                                        <Typography>→</Typography>
                                        <Chip
                                            label={log.changes.to}
                                            size="small"
                                            color="success"
                                        />
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }

        // CREATE: show created data
        if (log.changes.created || log.action === 'CREATE') {
            const data = log.changes.created || log.changes;
            const groupedFields = groupFields(data);

            return (
                <Box>
                    {Object.entries(groupedFields).map(([groupKey, group]) => (
                        <Accordion key={groupKey} defaultExpanded={groupKey !== 'scans'}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {group.icon}
                                    <Typography variant="subtitle2">{group.title}</Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                {groupKey === 'scans'
                                    ? renderScansTable(group.data.scans)
                                    : renderInfoTable(group.data)
                                }
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            );
        }

        // DELETE: show deleted data
        if (log.changes.deleted || log.action === 'DELETE') {
            const data = log.changes.deleted || log.changes;
            return (
                <Box>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            mb: 2,
                            backgroundColor: 'error.lighter',
                            borderColor: 'error.main'
                        }}
                    >
                        <Typography variant="subtitle2" color="error.main" gutterBottom>
                            {t('audit.deletedRecord', 'Deleted Record')}
                        </Typography>
                    </Paper>
                    {renderInfoTable(data)}
                </Box>
            );
        }

        // Default: show raw data in table format
        return renderInfoTable(log.changes);
    };

    // Render grouped data view
    const renderGroupedDataView = () => {
        const data = appointmentData;
        if (!data || typeof data !== 'object') {
            return (
                <Typography variant="body2" color="text.secondary">
                    {t('common.noData', 'No data available')}
                </Typography>
            );
        }

        const groupedFields = groupFields(data);

        return (
            <Box>
                {Object.entries(groupedFields).map(([groupKey, group]) => (
                    <Accordion key={groupKey} defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {group.icon}
                                <Typography variant="subtitle2">{group.title}</Typography>
                                <Chip
                                    label={Object.keys(group.data).length}
                                    size="small"
                                    variant="outlined"
                                />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            {groupKey === 'scans'
                                ? renderScansTable(group.data.scans)
                                : renderInfoTable(group.data)
                            }
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            {/* Dialog Title */}
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="h6">
                            {t('audit.logDetails', 'Audit Log Details')}
                        </Typography>
                        {getActionChip(log.action)}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title={t('common.copyAll', 'Copy All')}>
                            <IconButton size="small" onClick={handleCopyAll}>
                                <CopyIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {/* Log Meta Information */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        {/* Action Info */}
                        <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                                {t('audit.actionType', 'Action Type')}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>{getActionChip(log.action)}</Box>
                        </Box>

                        {/* Timestamp */}
                        <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                                {t('common.timestamp', 'Timestamp')}
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {log.formattedTimestamp || formatTimestamp(log.createdAt)}
                            </Typography>
                        </Box>

                        {/* User */}
                        <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                                {t('audit.performedBy', 'Performed By')}
                            </Typography>
                            {log.user ? (
                                <Box>
                                    <Typography variant="body2" fontWeight={500}>
                                        {log.user.username}
                                    </Typography>
                                    {log.user.email && (
                                        <Typography variant="caption" color="text.secondary">
                                            {log.user.email}
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {t('common.unknown', 'Unknown')}
                                </Typography>
                            )}
                        </Box>

                        {/* Log ID */}
                        <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                                {t('common.logId', 'Log ID')}
                            </Typography>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                                {log.id || log._id}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                {/* Tabs */}
                <Tabs
                    value={tabValue}
                    onChange={(e, newValue) => setTabValue(newValue)}
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                    <Tab label={t('audit.tabs.changes', 'Changes')} />
                    <Tab label={t('audit.tabs.allData', 'All Data')} />
                    <Tab label={t('audit.tabs.rawJson', 'Raw JSON')} />
                </Tabs>

                {/* Tab Panels */}
                <TabPanel value={tabValue} index={0}>
                    {renderChangesComparison()}
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    {renderGroupedDataView()}
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <Box display="flex" justifyContent="flex-end" mb={1}>
                        <Button
                            size="small"
                            startIcon={<CopyIcon />}
                            onClick={handleCopyChanges}
                        >
                            {t('common.copy', 'Copy')}
                        </Button>
                    </Box>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            maxHeight: 400,
                            overflow: 'auto'
                        }}
                    >
                        <Box
                            component="pre"
                            sx={{
                                m: 0,
                                fontSize: '0.75rem',
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}
                        >
                            {JSON.stringify(log.changes || log, null, 2)}
                        </Box>
                    </Paper>
                </TabPanel>
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">
                    {t('audit.appointmentId', 'Appointment')}: #{(log.appointment?.id || log.appointment?._id || appointmentData?._id)?.slice(-8) || 'N/A'}
                </Typography>
                <Button onClick={onClose} variant="outlined">
                    {t('common.close', 'Close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AuditLogDetailsDialog;